// ============================================================================
// TYPES
// ============================================================================

export enum ModalType {
  TOS = "tos",
  CRISIS_RESOURCES = "crisis_resources",
  CRISIS_PRACTITIONER = "crisis_practitioner",
  CONCERN_FLAG = "concern_flag",
  DETECTION_FEEDBACK = "detection_feedback",
}

export enum ModalPriority {
  CRITICAL = 1, // ToS - blocks everything
  HIGH = 2, // Crisis Resources - overrides concern
  MEDIUM_HIGH = 3, // Crisis Practitioner - after crisis resources
  MEDIUM = 4, // Concern
  LOW = 5, // Detection Feedback - lowest priority, after all safety modals
}

export type CrisisSeverity = "low" | "medium" | "high" | "critical";

export interface ModalState {
  type: ModalType;
  priority: ModalPriority;
  isVisible: boolean;
  isPending: boolean;
  data?: Record<string, unknown>;
}

export interface SafetyModalsState {
  modals: Record<ModalType, ModalState>;
  priorityQueue: ModalType[];
  tosAccepted: boolean;
  tosAcceptedAt?: string;
  tosVersion?: string;
  activeModal: ModalType | null;
  crisisDetected: boolean;
  crisisSeverity?: CrisisSeverity;
  isInitialized: boolean;
}

export interface ConcernFlagData extends Record<string, unknown> {
  analysisId?: string;
  screen?: "results" | "modal";
}

export interface CrisisPractitionerData extends Record<string, unknown> {
  /** Moderation event ID that triggered crisis detection */
  moderationEventId?: string;
  /** Crisis severity level */
  severity?: CrisisSeverity;
}

export interface DetectionFeedbackData extends Record<string, unknown> {
  /** Moderation event ID that triggered crisis detection */
  moderationEventId: string;
  /** Crisis severity level */
  severity?: CrisisSeverity;
  /**
   * Pattern IDs that matched (for feedback correlation, no PII)
   * Enables: "Which patterns trigger false positives?"
   * @example ["critical_intent_01", "high_harm_04"]
   */
  matchedPatternIds?: string[];
  /**
   * Semantic categories of matched patterns (for analytics grouping)
   * Enables: "Which semantic categories need tuning?"
   * @example ["suicide_intent", "self_harm"]
   */
  matchedSemanticCategories?: string[];
}

export interface SafetyModalsContextType {
  state: SafetyModalsState;
  // Modal actions
  openModal: (type: ModalType, data?: Record<string, unknown>) => void;
  closeModal: (type: ModalType) => void;
  closeActiveModal: () => void;
  // ToS specific
  acceptTos: (version: string) => Promise<void>;
  declineTos: (version: string) => void;
  checkTosAcceptance: () => Promise<boolean>;
  // Crisis specific
  triggerCrisisResources: (severity?: CrisisSeverity) => void;
  dismissCrisis: () => void;
  // Crisis Practitioner specific
  triggerPractitionerPrompt: (data?: CrisisPractitionerData) => void;
  // Concern specific
  triggerConcernFlag: (data?: ConcernFlagData) => void;
  // Detection Feedback specific (Phase 3)
  triggerDetectionFeedback: (data: DetectionFeedbackData) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const TOS_STORAGE_KEY = "@dramadetector/tos_accepted";
export const CURRENT_TOS_VERSION = "v1.0.0";

/**
 * MEDIUM FIX: Normalize ToS version for comparison
 * Handles variations: "v1.0.0" vs "1.0.0", case differences
 * Bug: Direct string comparison failed when version formats differed
 */
export function normalizeVersion(version: string | undefined): string {
  if (!version) return "";
  // Remove "v" prefix if present, lowercase, trim whitespace
  return version.toLowerCase().replace(/^v/, "").trim();
}

export const getPriority = (modalType: ModalType): ModalPriority => {
  switch (modalType) {
    case ModalType.TOS:
      return ModalPriority.CRITICAL;
    case ModalType.CRISIS_RESOURCES:
      return ModalPriority.HIGH;
    case ModalType.CRISIS_PRACTITIONER:
      return ModalPriority.MEDIUM_HIGH;
    case ModalType.CONCERN_FLAG:
      return ModalPriority.MEDIUM;
    case ModalType.DETECTION_FEEDBACK:
      return ModalPriority.LOW;
    default:
      return ModalPriority.MEDIUM;
  }
};

export const hasHigherPriority = (modalA: ModalType, modalB: ModalType): boolean => {
  return getPriority(modalA) < getPriority(modalB);
};
